import React, { useState } from 'react';
import ReactSelect from 'react-select';
import { differenceWith } from 'lodash';
import type { ScenarioTimeline_ShipFragment } from '../../graphql';
import {
  useAllVesselAttributesQuery,
  useCreateVaAssociationMutation,
  useDeleteVaaByIdMutation,
  useUpdatePlanningPageVesselInfoMutation,
} from '../../graphql';
import {
  Box,
  Button,
  FormField,
  FormSection,
  Heading,
  Input,
  Text,
} from '../ui';
import useEvent from '../hooks/useEvent';
import { reactSelectStyles } from '../../lib/form-util';
import { isDefined } from '../../lib/util';

type SelectOption = {
  label: string | undefined;
  value: number | undefined;
};

function VesselInfo({ vessel }: { vessel: ScenarioTimeline_ShipFragment }) {
  const id = vessel.id;
  const remarks = vessel.remarks ?? '';
  const sortId = vessel.sortId ? +vessel.sortId : undefined;
  const primaryAttribute = vessel.primaryAttributeId
    ? {
        label: vessel.vesselAttributeByPrimaryAttributeId?.name,
        value: vessel.primaryAttributeId,
      }
    : undefined;
  const existingAttributes =
    vessel.vesselAttributesAssociationsByShipId.nodes
      .filter(isDefined)
      .map((va) => ({
        id: va.id,
        label: va.vesselAttributeByVesselAttributeId?.name,
        value: va.vesselAttributeByVesselAttributeId?.id,
      })) ?? [];

  const [status, setStatus] = useState<'success' | 'error' | null>(null);
  const [errMessage, setErrMessage] = useState<string | null>(null);
  const [newRemarks, setRemarks] = useState(remarks);
  const [newSortId, setSortId] = useState(sortId);
  const [newVesselClass, setVesselClass] = useState<SelectOption | undefined>(
    primaryAttribute,
  );
  const [newAttributes, setAttributes] =
    useState<SelectOption[]>(existingAttributes);

  const [updateVesselInfo, { loading: upLoading }] =
    useUpdatePlanningPageVesselInfoMutation();
  const [createVA, { loading: createVALoading }] =
    useCreateVaAssociationMutation();
  const [deleteVA, { loading: deleteVALoading }] = useDeleteVaaByIdMutation();

  const updateSortIdHandler = useEvent(async () => {
    try {
      await updateVesselInfo({
        variables: {
          id,
          vessel: { sortId: newSortId },
        },
      });
      setErrMessage(null);
      setStatus('success');
    } catch (err) {
      setErrMessage(err.message);
      setStatus('error');
    }
  });

  const updateRemarksHandler = useEvent(async () => {
    try {
      await updateVesselInfo({
        variables: {
          id,
          vessel: {
            remarks: newRemarks,
          },
        },
      });
      setErrMessage(null);
      setStatus('success');
    } catch (err) {
      setErrMessage(err.message);
      setStatus('error');
    }
  });

  const updateAttributesHandler = useEvent(async () => {
    const toAdd = differenceWith(
      newAttributes,
      existingAttributes,
      (a, b) => a.value === b.value && a.label === b.label,
    );

    const toDelete = differenceWith(
      existingAttributes,
      newAttributes,
      (a, b) => a.value === b.value && a.label === b.label,
    );

    try {
      if (toAdd.length > 0) {
        await Promise.all(
          toAdd.map((va) => {
            if (!va.value) return;
            createVA({
              variables: {
                input: {
                  vesselAttributesAssociation: {
                    shipId: id,
                    vesselAttributeId: va.value,
                  },
                },
              },
            });
          }),
        );
      }
      if (toDelete.length > 0) {
        await Promise.all(
          toDelete.map((va) => {
            if (!va.id) return;
            deleteVA({
              variables: { associationId: va.id },
            });
          }),
        );
      }

      setErrMessage(null);
      setStatus('success');
    } catch (err) {
      setErrMessage(err.message);
      setStatus('error');
    }
  });

  const updateVesselClassHandler = useEvent(async () => {
    try {
      await updateVesselInfo({
        variables: {
          id,
          vessel: { primaryAttributeId: newVesselClass?.value },
        },
      });
      setErrMessage(null);
      setStatus('success');
    } catch (err) {
      setErrMessage(err.message);
      setStatus('error');
    }
  });

  const { data: allAttributesNodes, loading: vaLoading } =
    useAllVesselAttributesQuery();

  const allAttributes =
    allAttributesNodes?.allVesselAttributes?.nodes?.filter(isDefined) ?? [];

  const vesselAttributes =
    allAttributes
      .filter((va) => !va.isVesselClass)
      .map((va) => ({ label: va.name, value: va.id })) ?? [];
  const vesselClasses =
    allAttributes
      .filter((va) => va.isVesselClass)
      .map((va) => ({ label: va.name, value: va.id })) ?? [];

  return (
    <Box p={4}>
      <Heading mb={4} fontWeight="bold" fontSize={4}>
        Vessel Info
      </Heading>
      <FormSection display="block" muted>
        <Box style={{ display: 'grid', gridTemplateColumns: 'auto 1fr' }}>
          <FormField label="Sort Id">
            <Input
              type="number"
              width={100}
              disabled={upLoading}
              value={newSortId}
              onChange={(e) => {
                const value =
                  e.target.value !== '' ? +e.target.value : undefined;
                setStatus(null);
                setErrMessage(null);
                setSortId(value);
              }}
            />
            <Button
              disabled={upLoading}
              variant="secondary"
              alignSelf="flex-start"
              onPress={updateSortIdHandler}
              width={100}
              mt={3}
            >
              Set sort id
            </Button>
          </FormField>
          <FormField label="Remarks">
            <Input
              type="text"
              disabled={upLoading}
              value={newRemarks}
              onChange={(e) => {
                setStatus(null);
                setErrMessage(null);
                setRemarks(e.target.value);
              }}
            />
            <Button
              disabled={upLoading}
              variant="secondary"
              alignSelf="flex-start"
              onPress={updateRemarksHandler}
              mt={3}
            >
              Save remark
            </Button>
          </FormField>
        </Box>
      </FormSection>
      <FormSection display="block" muted>
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gridGap: 8,
          }}
        >
          <FormField label="Port cost class">
            <ReactSelect
              options={vesselClasses}
              isLoading={vaLoading}
              isDisabled={upLoading}
              value={newVesselClass}
              onChange={(selected) => {
                if (selected) {
                  setVesselClass(selected);
                }
              }}
              styles={reactSelectStyles}
            />
            <Button
              disabled={upLoading}
              variant="secondary"
              alignSelf="flex-start"
              onPress={updateVesselClassHandler}
              mt={3}
            >
              Set port cost class
            </Button>
          </FormField>
          <FormField label="Attributes">
            <ReactSelect
              isMulti
              options={vesselAttributes}
              isLoading={vaLoading}
              isDisabled={createVALoading || deleteVALoading}
              value={newAttributes}
              onChange={(selected) => {
                setAttributes([...selected]);
              }}
              styles={reactSelectStyles}
            />
            <Button
              disabled={upLoading}
              variant="secondary"
              alignSelf="flex-start"
              onPress={updateAttributesHandler}
              mt={3}
            >
              Set attributes
            </Button>
          </FormField>
        </Box>
      </FormSection>
      {status === 'success' ? (
        <Text fontWeight="bold" color="status.success">
          ✓ Saved successfully
        </Text>
      ) : status === 'error' ? (
        <Text fontWeight="bold" color="status.error">
          ✗ Error saving {errMessage}
        </Text>
      ) : null}
    </Box>
  );
}

export default VesselInfo;
